/**
 * Modules, read from the repo.
 *
 * The same three questions the capsule and book queries answer, asked about
 * playlists: *what is this module* (by its own address), *which module is this
 * post* (the slow way round, for a post whose back-reference never landed), and
 * *what has this account made* (every module it holds, drafts included).
 *
 * Reading records rather than AppView views is the same decision the pickers
 * make, and it is worth stating for the third one: a module's own fields — its
 * playlist, its subject, its level, whether it is a draft — are what a profile's
 * tab, a picker and a classroom all need, and a *post* carries a back-reference to
 * a module and none of those. One `listRecords` answers every question a chooser
 * has at once.
 */
import {AtUri} from '@atproto/api'
import {useQuery} from '@tanstack/react-query'

import {MODULE_COLLECTION} from '#/lib/modulePlaylist'
import {moduleViewOf, type ModuleRecordView} from '#/lib/moduleRecord'
import {createQueryKey} from '#/state/queries/util'
import {useAgent} from '#/state/session'

const moduleByUriQueryKeyRoot = 'moduleByUri'
const moduleByPostQueryKeyRoot = 'moduleByPost'
const actorModulesQueryKeyRoot = 'actorModules'

export const createModuleByUriQueryKey = (moduleUri: string) =>
  createQueryKey(moduleByUriQueryKeyRoot, {moduleUri})

/**
 * Fetches a module record directly by its AT URI.
 *
 * Used by the module screen, its carousel, the leaderboard and the studio — all
 * of which know the address, because the post's back-reference gives it. The PDS
 * is asked rather than an AppView view, so the feature keeps working on a
 * deployment whose AppView has not been taught about modules.
 */
export function useModuleByUriQuery(moduleUri: string | undefined) {
  const agent = useAgent()

  return useQuery({
    queryKey: createModuleByUriQueryKey(moduleUri ?? ''),
    enabled: !!moduleUri,
    queryFn: async (): Promise<ModuleRecordView> => {
      const atUri = new AtUri(moduleUri!)
      const res = await agent.com.atproto.repo.getRecord({
        repo: atUri.hostname,
        collection: atUri.collection,
        rkey: atUri.rkey,
      })
      return moduleViewOf(res.data.uri, res.data.cid ?? '', res.data.value)
    },
    staleTime: 1000 * 60 * 5, // 5 min
  })
}

export const createModuleByPostQueryKey = (postUri: string) =>
  createQueryKey(moduleByPostQueryKeyRoot, {postUri})

/**
 * Finds the module a post belongs to, by looking through its author's records.
 *
 * The slow way round, and the one that exists because publishing a module is two
 * writes: the post first, then the same post again with the module's
 * back-reference on it. A creator whose second write failed has a perfectly good
 * published playlist and a post that points at nothing, and "the module I just
 * published does not appear" is not an acceptable answer to that.
 *
 * Authors rarely have many modules, so this pages their records and stops at the
 * first whose `post` points back. It only runs when the post says nothing, so a
 * healthy feed never makes this request.
 */
export function useModuleByPostQuery(postUri: string, authorDid: string) {
  const agent = useAgent()

  return useQuery({
    queryKey: createModuleByPostQueryKey(postUri),
    enabled: !!postUri && !!authorDid,
    queryFn: async (): Promise<ModuleRecordView | null> => {
      let cursor: string | undefined
      while (true) {
        const res = await agent.com.atproto.repo.listRecords({
          repo: authorDid,
          collection: MODULE_COLLECTION,
          limit: 50,
          cursor,
        })
        const match = res.data.records.find(record => {
          const value = record.value as Record<string, unknown> | undefined
          const post = value?.post as {uri?: unknown} | undefined
          return !!post && post.uri === postUri
        })
        if (match) {
          return moduleViewOf(
            match.uri,
            match.cid ?? '',
            match.value as Record<string, unknown>,
          )
        }
        cursor = res.data.cursor
        if (!cursor) return null
      }
    },
    staleTime: 1000 * 60 * 5, // 5 min
  })
}

/**
 * One of an author's modules, as a grid or a picker needs it.
 *
 * Deliberately the record itself, plus the one fact a reader of the *list* needs
 * and the record does not carry in a convenient form: how many capsules are in
 * the playlist. A profile's tab draws a count on the card, and asking it of
 * `items.length` at every call site is how two screens come to disagree about a
 * module with one place left to fill.
 *
 * The same three fields the capsule and book lists expose — `status`, `postUri`
 * and `visibility` — are here for the same reasons: a draft is shown to the
 * creator alone, a record marked `classroom` belongs to a room, and a published
 * record with no post is work nobody could open.
 */
export type ActorModuleRecord = ModuleRecordView & {
  /** How many places the playlist has, filled or not. */
  itemCount: number
}

export const createActorModulesQueryKey = (actor: string) =>
  createQueryKey(actorModulesQueryKeyRoot, {actor})

/**
 * Every module record an author has written, drafts included.
 *
 * Drafts are returned on purpose, and the caller leaves out what it must: the
 * creator's own profile tab shows them (a module is assembled over days, and
 * finding the one you were building is the first thing a creator wants from their
 * own page), while a visitor's page and any picker of things to play do not.
 */
export function useActorModulesQuery(actor: string | undefined) {
  const agent = useAgent()

  return useQuery({
    queryKey: createActorModulesQueryKey(actor ?? ''),
    enabled: !!actor,
    queryFn: async (): Promise<ActorModuleRecord[]> => {
      const out: ActorModuleRecord[] = []
      let cursor: string | undefined
      while (true) {
        const res = await agent.com.atproto.repo.listRecords({
          repo: actor!,
          collection: MODULE_COLLECTION,
          limit: 100,
          cursor,
        })
        for (const record of res.data.records) {
          const value = (record.value ?? {}) as Record<string, unknown>
          const view = moduleViewOf(record.uri, record.cid ?? '', value)
          out.push({...view, itemCount: view.items.length})
        }
        if (!res.data.cursor || res.data.records.length < 100) break
        cursor = res.data.cursor
      }
      return out
    },
    staleTime: 1000 * 60 * 5, // 5 min
  })
}
